import { AnyAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { CustomerGroupModel } from '@app/interfaces/customer/group';
import { createCustomerGroupApi, deleteCustomerGroupApi, getCustomerGroupsApi, updateCustomerGroupApi } from '@app/api/customerGroups.api';
import { message } from 'antd';
import { showError } from '@app/utils/message';

export interface CustomerGroupState {
  data: CustomerGroupModel[];
  loading: boolean;
  error: string | null;
}

const initialState: CustomerGroupState = {
  data: [],
  loading: false,
  error: null,
};

interface ResponseData {
  data: CustomerGroupModel;
  message: string;
}

// Thunks
export const getCustomerGroups = createAsyncThunk<CustomerGroupModel[]>(
  'customer_group/getCustomerGroups',
  async (_, { rejectWithValue }) => {
    try {
      const data = await getCustomerGroupsApi()
      return data || [];

    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

export const createCustomerGroup = createAsyncThunk<ResponseData, CustomerGroupModel>(
  'customer_group/createCustomerGroup', async (data) => {
  const response = await createCustomerGroupApi(data);
  return response;
});

export const updateCustomerGroup = createAsyncThunk<ResponseData, { customerGoupId: number; formData: CustomerGroupModel }>(
  'customer_group/updateCustomerGroup',
  async ({ customerGoupId, formData }) => {
    const response = await updateCustomerGroupApi(customerGoupId, formData);
    return response;
  },
);

export const deleteCustomerGroup = createAsyncThunk<{ customerGoupId: number; response: any }, number>(
  'customer_group/deleteCustomerGroup',
  async (customerGoupId, { rejectWithValue }) => {
    try {
      const response = await deleteCustomerGroupApi(customerGoupId);
      return { customerGoupId, response };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

const isRejectedAction = (action: AnyAction) => {
  return action.type.endsWith('rejected');
};

// Slice
export const customerGroupSlice = createSlice({
  name: 'customer_group',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getCustomerGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCustomerGroups.fulfilled, (state, action) => {
        state.data = action.payload
        state.loading = false;
      })
      .addCase(createCustomerGroup.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;
        state.data.unshift(data);
        message.success(newMessage);
      })
      .addCase(updateCustomerGroup.fulfilled, (state, action) => {
        const { data, message: newMessage } = action.payload;
        const dataId = typeof data.id === 'string' ? parseInt(data.id, 10) : data.id;

        const updatedIndex = state.data.findIndex((item) => item.id === dataId);
        if (updatedIndex !== -1) {
          state.data[updatedIndex] = data;
          message.success(newMessage);
        }
      })
      .addCase(deleteCustomerGroup.fulfilled, (state, action) => {
        const { customerGoupId, response } = action.payload;

        state.data = state.data.filter((supplier) => supplier.id !== customerGoupId);
        message.success(response.message);
      })
      .addMatcher(isRejectedAction, (state, action) => {
        state.loading = false;
        showError(action.payload?.message || action.payload?.error || action?.error?.message);
      });
    }
  }
);

export default customerGroupSlice.reducer;